import { IdSchema } from "@hisani/core";
import { z } from "zod";
import { BasisPointsSchema, basisPointsSumToWhole, evenBasisPointSplit, splitAmountByBasisPoints, } from "./percentage-allocation.js";
/**
 * How a bill's cost is attributed to horses
 * (`docs/products/stable.md` "Accounting"):
 *
 * - `HORSE_LINK` — "Vet/Farrier bills link to one or more specific horses,
 *   drawn from the invoice or entered manually." No percentage is entered by
 *   the user; the cost is split evenly across the linked horses (a
 *   deliberate, documented interpretation — the spec does not say how a
 *   multi-horse link divides cost, and equal split is the least-surprising
 *   default for, e.g., one vet call-out covering several horses). Internally
 *   normalized to the same basis-point shape `PERCENTAGE` uses
 *   ({@link evenBasisPointSplit}), so one invariant (shares sum to exactly
 *   10,000 bp) covers both modes.
 * - `PERCENTAGE` — "General bills can be allocated across one or more horses
 *   by percentage." The user enters explicit basis-point shares that must
 *   sum to exactly 10,000.
 * - `UNLINKED` — "or left unlinked as a general Stable expense." No horse,
 *   no owner, never appears on any invoice draft.
 *
 * Allocation mode is a property of the bill entry, not a hard function of
 * its expense category — a Trainer categorizes a bill (Feed, Vet, ...) and
 * independently chooses how it is allocated, since a category alone cannot
 * determine intent (e.g. a farrier bill covering the whole yard's routine
 * trim day is legitimately a `PERCENTAGE` allocation across many horses,
 * not a `HORSE_LINK`).
 */
export const BILL_ALLOCATION_MODES = [
    "HORSE_LINK",
    "PERCENTAGE",
    "UNLINKED",
];
export const BillAllocationModeSchema = z.enum(BILL_ALLOCATION_MODES);
const horseLinkInputSchema = z.object({
    mode: z.literal("HORSE_LINK"),
    horseIds: z.array(IdSchema).min(1).max(50),
});
const percentageInputSchema = z.object({
    mode: z.literal("PERCENTAGE"),
    shares: z
        .array(z.object({ horseId: IdSchema, basisPoints: BasisPointsSchema }))
        .min(1)
        .max(50),
});
const unlinkedInputSchema = z.object({ mode: z.literal("UNLINKED") });
export const BillAllocationInputSchema = z.discriminatedUnion("mode", [
    horseLinkInputSchema,
    percentageInputSchema,
    unlinkedInputSchema,
]);
export class BillAllocationError extends Error {
}
/**
 * Normalizes any of the three allocation inputs into the persisted
 * `BillHorseShare[]` shape (empty for `UNLINKED`), enforcing that a
 * horse never appears twice and that basis points sum to exactly 10,000
 * whenever any horse is linked. This is the single point both the
 * application layer and its tests exercise the "must sum to exactly 100%"
 * invariant through — the persistence layer enforces the same rule again
 * with a database CHECK constraint as defense in depth (see
 * `packages/database/AGENTS.md`: "Use PostgreSQL constraints for
 * invariants the database can enforce").
 */
export function resolveBillAllocation(input) {
    if (input.mode === "UNLINKED")
        return [];
    if (input.mode === "HORSE_LINK") {
        const uniqueHorseIds = [...new Set(input.horseIds)];
        if (uniqueHorseIds.length !== input.horseIds.length) {
            throw new BillAllocationError("A horse cannot be linked more than once.");
        }
        const shares = evenBasisPointSplit(uniqueHorseIds.length);
        return uniqueHorseIds.map((horseId, index) => ({
            horseId,
            basisPoints: shares[index],
        }));
    }
    const uniqueHorseIds = new Set(input.shares.map((share) => share.horseId));
    if (uniqueHorseIds.size !== input.shares.length) {
        throw new BillAllocationError("A horse cannot be allocated more than once.");
    }
    if (!basisPointsSumToWhole(input.shares.map((share) => share.basisPoints))) {
        throw new BillAllocationError("Percentage allocations must sum to exactly 100%.");
    }
    return input.shares;
}
/**
 * Wraps {@link splitAmountByBasisPoints} to attach each share's exact
 * minor-unit amount, handling the `UNLINKED` case (`shares.length === 0`)
 * without tripping that function's "must sum to 10,000" precondition —
 * an empty allocation trivially allocates nothing.
 */
export function allocateBillAmount(amountMinor, shares) {
    if (shares.length === 0)
        return [];
    const amounts = splitAmountByBasisPoints(amountMinor, shares.map((share) => share.basisPoints));
    return shares.map((share, index) => ({
        ...share,
        amountMinor: amounts[index],
    }));
}
